import { useState, useCallback, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useActivationObjectCreateMutation } from "../../../../api/activationObject/hooks"
import { useUserAuthentification, useUserQuery } from "../../../../api/user/hooks"
import { AppAuthProviderProps, useAppAuthProviderProps } from "../types"
import { useQueryClient } from "react-query"

const useAppAuthProvider = (props: useAppAuthProviderProps): AppAuthProviderProps => {
    const queryClient = useQueryClient()

    const [synonym, setSynonym] = useState<string>()
    const [token, setToken] = useState<string>()
    const { data: user } = useUserQuery(token)
    const authMutation = useUserAuthentification()
    const createMutation = useActivationObjectCreateMutation()

    const { search } = useLocation()
    const queryString = new URLSearchParams(search)
    const guidQ = queryString.get("guid")
    const hashQ = queryString.get("hash")
    const synonymQ = queryString.get("synonym")

    const createReactivationObject = useCallback(
        (_synonym: string) => {
            if (_synonym) {
                createMutation.mutate(_synonym)
            }
        },
        [createMutation]
    )

    const reactivationObjectQueryState = queryClient.getQueryState(["activation-object"])

    const reactivationObjectNotFound = reactivationObjectQueryState?.status === "error"

    const synonymLS = localStorage.getItem("synonym")
    const tokenLS = localStorage.getItem("token")

    const tokenDataAvailable = !!synonymLS && !!tokenLS

    const handleNotFoundObject = useCallback(() => {
        if (tokenDataAvailable && reactivationObjectNotFound) {
            setSynonym(synonymLS)
            setToken(tokenLS)
            createReactivationObject(synonymLS)
        }
    }, [createReactivationObject, reactivationObjectNotFound, synonymLS, tokenDataAvailable, tokenLS])

    // init - set synonym/token from query/localStorage
    useEffect(() => {
        if (synonymQ) {
            localStorage.removeItem("synonym")
            localStorage.removeItem("token")
            setSynonym(synonymQ)
            setToken(undefined)
            return
        }

        if (tokenDataAvailable) {
            setSynonym(synonymLS)
            setToken(tokenLS)
        }

        handleNotFoundObject()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [synonymQ])

    // store to localStorage
    useEffect(() => {
        if (synonym) {
            //console.log('setting synonym to LS')
            localStorage.setItem("synonym", synonym)
        }
        if (token) {
            //console.log('setting token to LS', token)
            localStorage.setItem("token", token)
        }
    }, [synonym, token])

    // authorize
    useEffect(() => {
        if (guidQ && hashQ && synonym && token === undefined && authMutation.status === "idle") {
            authMutation.mutate({ apiCode: guidQ, hash: hashQ })
        }
    }, [authMutation, guidQ, hashQ, synonym, token])

    // set token from mutation
    useEffect(() => {
        if (authMutation.isSuccess && authMutation.data) {
            //console.log('auth success, setting token')
            setToken(authMutation.data.tokenCode)
        }
    }, [authMutation.data, authMutation.isSuccess])

    const isAuthenticated =
        synonym !== undefined && token !== undefined && synonym === localStorage.getItem("synonym") && token === localStorage.getItem("token")
    //console.log('isAuthenticated', isAuthenticated)
    // is authenticated, if there is synonom and token, and also present in localStorage
    return {
        contextValue: {
            isAuthenticated,
            user,
            token: token || undefined,
            synonym,
        },
    }
}

export default useAppAuthProvider
